import {
  Avatar,
  Input,
  Row,
  Select,
  Col,
  Button,
  Card,
  Flex,
  Table,
  Typography,
} from "antd";
import React, { useState } from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styles from "./ServiceProvider.module.scss";
import { dateObjToDispViewDate } from "../../../../../utils/utils";
import Spinner from "../../../../common/spinner/Spinner";
import { getServiceProvidersAction } from "../../../../../redux/services/service-providers/serviceProvidersServices";
import { appAssetsConfig } from "../../../../../constants/config/appAssetsConfig";
import { appConstConfig } from "../../../../../constants/config/appConstConfig";
import { routes } from "../../../../../constants";
import Actions from "../../../../../redux/actions";
import { baseRouteMakerForSuperAdmin } from "../../../../../constants/routeConsts";
import { SearchOutlined,ShopOutlined } from "@ant-design/icons";
import { colors } from "../../../../../constants/colors";
import { getCountryAction } from "../../../../../redux/services/auth/authServices";
import useDebounce from "../../../../../hooks/useDebounce";

const ServiceProvider = () => {
  /* i18n dependencies */
  const { t } = useTranslation();
  /* i18n dependencies */

  /* dispatch an action */
  const dispatch = useDispatch();
  /* dispatch an action */

  /* For navigating between pages */
  const navigate = useNavigate();
  /* For navigating between pages */

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  /* loading dependencies */

  /* table dependencies */
  const { serviceProviders } = useSelector((state) => state.serviceProviders);
  const [columns, setColumns] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [pagination, setPagination] = useState({
    pageNo: appConstConfig.DEFAULT_TABLE_PAGE_NUMBER,
    pageSize: appConstConfig.DEFAULT_TABLE_PAGE_SIZE,
  });
  const paginationObj = {
    total: serviceProviders?.total,
    current: pagination?.pageNo,
    pageSize: pagination?.pageSize,
    onChange: (pageNo, pageSize) => setPagination({ pageNo, pageSize }),
    pageSizeOptions: appConstConfig.DEFAULT_PAGE_SIZE_OPTIONS_ARR,
    showSizeChanger: true,
  };
  /* table dependencies */

  /* filter dependencies */
  const { debouncedValue: debouncedSearchValue, debounceLoading } = useDebounce(
    searchValue,
    500
  );
  const { countryList } = useSelector((state) => state?.auth);
  const [selectedCountryObj, setSelectedCountryObj] = useState(null);

  /* on Country changed */
  const onCountryChanged = (value, element) => {
    const selectedCountryObject = countryList?.find(
      (item) => item._id === value
    );
    setSelectedCountryObj(selectedCountryObject);
  };

  const selectListApiCalls = () => {
    // Country select list
    dispatch(getCountryAction());
  };
  /* function definition to get table data */
  const handleGetTableData = () => {
    setLoading(true);
    const selCountryIdArr = new Array(selectedCountryObj?._id);
    const dataToBeSent = {
      page: debouncedSearchValue !== "" ? 1 : pagination?.pageNo,
      limit: pagination?.pageSize,
      search: debouncedSearchValue,
      country: selCountryIdArr,
    };
    dispatch(getServiceProvidersAction(dataToBeSent)).then(() => {
      setLoading(false);
    });
  };
  /* function definition to get table data */

  useEffect(() => {
    selectListApiCalls(null);
    handleGetTableData();
  }, [pagination, debouncedSearchValue, selectedCountryObj]);

  /* initial dependencies rendering */
  useEffect(() => {
    /* table column config */
    const tempTableColumns = [
      {
        title: t("Modules.Provider.serviceProviderId"),
        dataIndex: "organization_number",
        key: "organization_number",
        align: "center",
      },
      {
        title: t("Modules.WalletManagement.serviceProvider"),
        dataIndex: "name",
        key: "name",
        render: (_,record) => {
          return (
            <Flex gap="1rem" align="center">
              <Avatar
                size="large"
                src={record?.logo_image}
                key={record?.logo_image}
                icon={<ShopOutlined />}
              />
               {
                record?.name
            ? `${record?.name}`
            : "-"
               }
            </Flex>
          );
        },
      },
      {
        title: t("appointmentInfo.directorName"),
        dataIndex: "director_name",
        key: "director_name",
        align: "center",
      },
      {
        title: t("ProfileSetup.cityLabel"),
        dataIndex: "address",
        key: "address",
        render: (address) => `${address?.city?.name || "-"}`,
      },
      {
        title: t("ProfileSetup.countryLabel"),
        dataIndex: "country",
        key: "country",
        render: (country) => `${country?.emoji +"  "+ country?.name || "-"}`,
      },
      {
        title: t("staffList.email"),
        dataIndex: "owner_details",
        key: "email",
        render: (owner_details) => (owner_details && owner_details?.email ? `${owner_details?.email}` : "-"),
      },
      {
        title: t("staffList.phone"),
        dataIndex: "owner_details",
        key: "phone_number",
        render: (owner_details) =>
        owner_details && owner_details?.phone
            ? `+${owner_details?.phone?.country_code}-${owner_details?.phone?.phone_number}`
            : "-",
      },
      {
        title: t("Modules.Provider.dateofJoining"),
        dataIndex: "created_at",
        key: "created_at",
        render: (_, record) => (
          <p>{dateObjToDispViewDate(record?.created_at, "ddmmyyyy")}</p>
        ),
      },
      {
        title: t("AppModuleNames.Provider.providerInfo"),
        key: "action",
        align: "center",
        render: (_, record) => {
          return (
            <>
              <Typography.Link onClick={(e) => handleViewDetails(e, record)}>
                View
              </Typography.Link>
            </>
          );
        },
      },
    ];
    /* table column config */
    setColumns(tempTableColumns);
  }, []);
  /* initial dependencies rendering */

  const handleViewDetails = (e, row) => {
    dispatch(
      Actions.createAction(Actions.SELECTED_SERVICE_PROVIDER_ID, {
        selectedServiceProviderId: "",
        navigateBack: "",
      })
    );
    navigate(
      baseRouteMakerForSuperAdmin(routes.SERVICE_PROVIDER_DETAILS_DETAILS)
    );
    if (
      row?._id &&
      row?._id !== "" &&
      row?._id !== null &&
      row?._id !== undefined
    ) {
      const selectedServiceProviderId = row?._id;
      dispatch(
        Actions.createAction(Actions.SELECTED_SERVICE_PROVIDER_ID, {
          selectedServiceProviderId,
          navigateBack: baseRouteMakerForSuperAdmin(routes.SERVICE_PROVIDER),
        })
      );
    }
  };

  return (
    <div className={styles.serviceProviderContainer}>
      <Flex vertical gap="2rem">
        <Row gutter={24} justify="space-between">
          <Col span={8} style={{ display: "flex", alignItems: "center" }}>
            <Input
              size="large"
              suffix={<SearchOutlined />}
              placeholder={t("CommonUtils.searchByNameEmail")}
              onChange={(event) => setSearchValue(event.target.value)}
              style={{ width: "370px" }}
            />
          </Col>
          <Col span={8} style={{ display: "flex", alignItems: "center" }}>
            <Select
              style={{ width: "370px" }}
              placeholder={t("ProfileSetup.countryPlaceholder")}
              onChange={(value, element) => onCountryChanged(value, element)}
              allowClear
              showSearch
              filterOption={(input, option) =>
                (option?.children ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {countryList &&
                countryList?.map((item) => (
                  <Select.Option key={item?._id} value={item?._id}>
                  {item?.name}
                  </Select.Option>
                ))}
            </Select>
          </Col>
          <Col span={8}>
            <Card
              bordered
              hoverable
              style={{ width: 240, padding: 0, borderRadius: 20, border: `1px solid ${colors.oceanBlue}` }}
            >
              <Row gutter={[24, 24]} align="middle" justify="center">
                <Col span={8}>
                  <Button
                    type="primary"
                    shape="circle"
                    size="middle"
                    icon={<ShopOutlined style={{ fontSize: 22 }} />}
                  />
                </Col>
                <Col span={16}>
                  <Flex vertical>
                    <Typography.Title level={4}>
                      {t("providerCount")}
                    </Typography.Title>
                    <Typography.Title
                      level={2}
                      style={{ margin: 0, color: colors.electricBlue }}
                    >
                      {serviceProviders?.total || 0}
                    </Typography.Title>
                  </Flex>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
        {loading ? (
          <Spinner />
        ) : (
          <Table
            dataSource={serviceProviders?.data}
            columns={columns}
            pagination={paginationObj}
          />
        )}
      </Flex>
    </div>
  );
};

export default ServiceProvider;
